import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Components
import LoginModal from './LoginModal';
import SignupModal from './SignupModal';
import LoginModalHome from './LoginModalHome';

// Icons
import LocationIcon from '../assets/location-icon.svg';
import DownArrow from '../assets/down-arrow.svg';
import TranslatorIcon from '../assets/language-convter-white-icon.svg';
import ProfileIcon from '../assets/profile-avator.svg';
import CartIcon from '../assets/cart-icon.svg';
import { ShoppingCart } from 'lucide-react';
import SearchIcon from '../assets/search.svg';
import { FaAngleDown } from 'react-icons/fa6';
import SelectLocation from './SelectLocationModal';
import CartModal from './CartModal';
import FilterModal from './FilterModal';
import FilterIcon from '../assets/filter-icon.svg';
import { useUser } from '../contexts/user';
import { useCart } from '../contexts/cart';
import { useSearch } from '../contexts/search';
import { useFilterConfig } from '../contexts/filters';
import { FILTERS_EVENT, FILTERS_STORAGE_KEY } from '../constants/filters';
import { OPEN_LOGIN_EVENT } from '../constants/auth';
import { getApiErrorMessage } from '../utils/apiError';
import { useSignupInvite } from '../contexts/buyer-invite';
import { FaArrowLeft, FaChevronLeft } from 'react-icons/fa';
import api from '../api/api';
import useToast from '../hooks/useToast';
import Toast from './Toast';

const Header = ({ storeName, merchantConfigId, categoryName }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);
  const [isLoginHomeModalOpen, setIsLoginHomeModalOpen] = useState(false);
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [isCartModalOpen, setIsCartModalOpen] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const dropdownRef = useRef(null);
  const desktopDropdownRef = useRef(null);
  const { user, idToken, adminImpersonate } = useUser();
  const { uniqueItems } = useCart();
  const location = useLocation();
  const { searchTerm, setSearchTerm } = useSearch();
  const { mode: filterMode } = useFilterConfig();
  const { prefillData, setPrefillData } = useSignupInvite();
  const { toastMessage, toastType, showToast } = useToast();

  // Open Location Modal (close other modals)
  const handlelocationModal = () => {
    const opening = !isLocationModalOpen;
    if (opening) {
      setIsLoginModalOpen(false);
      setIsSignupModalOpen(false);
      setIsLoginHomeModalOpen(false);
      setIsCartModalOpen(false);
    }
    setIsLocationModalOpen(opening);
  };

  // Open Cart Modal (close other modals)
  const handleCartModal = () => {
    const opening = !isCartModalOpen;
    if (opening) {
      setIsLocationModalOpen(false);
      setIsLoginModalOpen(false);
      setIsSignupModalOpen(false);
      setIsLoginHomeModalOpen(false);
      setIsFilterModalOpen(false);
    }
    setIsCartModalOpen(opening);
  };

  const handleApplyFilters = (selected) => {
    try {
      sessionStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(selected));
    } catch { /* sessionStorage unavailable */ }
    window.dispatchEvent(new CustomEvent(FILTERS_EVENT, { detail: selected }));
  };

  const handleFilterModal = () => {
    const opening = !isFilterModalOpen;
    if (opening) {
      setIsLocationModalOpen(false);
      setIsLoginModalOpen(false);
      setIsSignupModalOpen(false);
      setIsLoginHomeModalOpen(false);
      setIsCartModalOpen(false);
    }
    setIsFilterModalOpen(opening);
  };

  // Handle Login Modal (close other modals)
  const handleToggleLoginModal = () => {
    const opening = !isLoginModalOpen;
    if (opening) {
      setIsLocationModalOpen(false);
      setIsCartModalOpen(false);
    }
    setIsLoginModalOpen(opening);
  };

  // Handle Signup Modal
  const handleToggleSignupModal = () => {
    setIsSignupModalOpen(!isSignupModalOpen);
  };

  // Listen for cross-component requests to open the login modal (e.g. the
  // sign-in CTA inside the product details modal).
  useEffect(() => {
    const handler = () => {
      setIsLocationModalOpen(false);
      setIsCartModalOpen(false);
      setIsSignupModalOpen(false);
      setIsLoginHomeModalOpen(false);
      setIsLoginModalOpen(true);
    };
    window.addEventListener(OPEN_LOGIN_EVENT, handler);
    return () => window.removeEventListener(OPEN_LOGIN_EVENT, handler);
  }, []);

  useEffect(() => {
    if (prefillData) {
      handleToggleSignupModal();
    }
  }, [prefillData]);

  // Handle automatic opening of the login modal on home page if session expired
  useEffect(() => {
    if (sessionStorage.getItem('sessionExpired') === 'true') {
      sessionStorage.removeItem('sessionExpired');
      setIsLoginModalOpen(true);
    }
  }, []);

  // Handle Login Home Modal
  const handleToggleLoginModalHome = () => {
    setIsLoginHomeModalOpen(!isLoginHomeModalOpen);
  };

  const handleEmailSubmit = async (email) => {
    try {
      const response = await api.post('/user/check-email', { email });
      const { exists } = response.data.response.data;

      if (exists) {
        setIsLoginHomeModalOpen(true);
        setIsSignupModalOpen(false);
      } else {
        setIsLoginHomeModalOpen(false);
        setIsSignupModalOpen(true);
      }
    } catch (error) {
      showToast(getApiErrorMessage(error, t), 'error');
    }
  };

  // LanguageSwitcher dropdown — close on outside click for either trigger
  useEffect(() => {
    const handleClickOutside = (event) => {
      const insideMobile = dropdownRef.current && dropdownRef.current.contains(event.target);
      const insideDesktop = desktopDropdownRef.current && desktopDropdownRef.current.contains(event.target);
      if (!insideMobile && !insideDesktop) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle language change
  const handleLanguageChange = (language) => {
    i18n.changeLanguage(language);
    setIsDropdownOpen(false);
  };

  const formatLineOne = (addr, maxLength = 30) => {
    if (!addr || typeof addr.formattedAddress !== 'string') {
      return '';
    }

    const parts = addr.formattedAddress.split(',');
    const line = parts.length > 0 ? parts[0].trim() : '';

    if (line.length <= maxLength) {return line;}

    return line.slice(0, maxLength).trimEnd() + '…';
  };

  const formatLineTwo = (addr, maxLength = 30) => {
    if (!addr || typeof addr.formattedAddress !== 'string') {
      return '';
    }

    const parts = addr.formattedAddress.split(',');
    const line = parts.length > 1 ? parts.slice(1).join(',').trim() : '';

    if (line.length <= maxLength) {return line;}

    return line.slice(0, maxLength).trimEnd() + '…';
  };

  const isHomePage = location.pathname === '/home';
  const isSearchPage = location.pathname === '/search';
  const isMerchantDetailsPage = location.pathname.startsWith('/merchant-details/');
  const isCheckoutPage = location.pathname === '/checkout';
  const showFilterButton = Boolean(filterMode);
  const isProfileOrdersPage = location.pathname === '/profile/orders';
  const isProfileManageAddressPage = location.pathname === '/profile/manage-address';
  const isProfileLanguagePage = location.pathname === '/profile/change-language';
  const isMerchantCategoryPage = location.pathname.startsWith('/category/');

  return (
    <header className='sticky bg-white z-50 top-0 left-0 w-full lg:h-[65px] lg:py-1 px-2 md:px-4 flex items-center justify-between nav-shadow'>
      <div className='flex items-center justify-between w-full gap-4 lg:gap-6 xl:gap-10'>
        
        {/* HEADER FOR MOBILE */}
        {isHomePage && (
          <>
            <div className="lg:hidden w-full flex flex-col gap-3 px-2 py-2 mb-2">
              {/* Top row */}
              <div className="w-full flex items-center justify-between">
                {/* Select Address */}
                <div
                  className="flex items-center cursor-pointer"
                  onClick={handlelocationModal}
                >
                  <img src={LocationIcon} alt="LocationIcon" className="w-5 h-5 mr-1" />

                  {/* Location Info */}
                  {user && user.locations && user.locations.length > 0 ? (
                    <div className="flex flex-col leading-tight min-w-0">
                      <span className="text-s font-bold truncate max-w-[140px] sm:max-w-[200px]">
                        {formatLineOne(user.locations[0])}
                      </span>
                      <span className="text-xs truncate max-w-[140px] sm:max-w-[200px]">
                        {formatLineTwo(user.locations[0])}
                      </span>
                    </div>
                  ) : (
                    <span className="text-xs text-gray-600 truncate max-w-[140px] sm:max-w-[200px]">
                      {t('Select delivery location')}
                    </span>
                  )}
                  <img src={DownArrow} alt="DownArrow" className="w-4 h-4 ml-1" />
                </div>

                {/* Language switcher for mobile */}
                <div className='relative' ref={dropdownRef}>
                  <button
                    className='h-11 px-2 bg-black flex items-center gap-1 capitalize font-medium rounded text-xs text-white'
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    aria-label="Change language"
                  >
                    <img src={TranslatorIcon} alt="Language" className='h-[12px] w-3' />
                    <p>{i18n.language === 'en' ? 'EN' : 'FR'}</p>
                    <FaAngleDown className="text-[10px]" />
                  </button>

                  {isDropdownOpen && (
                    <ul className='absolute right-0 py-2 bg-white top-9 min-w-[120px] rounded shadow-lg z-50'>
                      <li>
                        <button
                          className={`min-h-12 px-4 text-start font-normal mb-1 text-base transition-colors duration-300 w-full cursor-pointer ${i18n.language === 'en' ? 'bg-gray-200' : 'hover:bg-[#e0e0e0]'}`}
                          onClick={() => handleLanguageChange('en')}
                        >
                          English
                        </button>
                      </li>
                      <li>
                        <button
                          className={`min-h-12 px-4 text-start font-normal text-base transition-colors duration-300 w-full cursor-pointer ${i18n.language === 'fr' ? 'bg-gray-200' : 'hover:bg-[#e0e0e0]'}`}
                          onClick={() => handleLanguageChange('fr')}
                        >
                          Français
                        </button>
                      </li>
                    </ul>
                  )}
                </div>

                {/* Cart icon for mobile */}
                {location.pathname !== '/checkout' && (
                  <button
                    onClick={handleCartModal}
                    className="relative flex items-center justify-center w-10 h-10"
                    aria-label="Cart"
                  >
                    <ShoppingCart className="w-5 h-5" />
                    {uniqueItems > 0 && (
                      <span className="absolute -top-1 -right-1 bg-[#ff1e1e] w-[18px] h-[18px] flex items-center justify-center rounded-full text-[10px] border-2 border-white text-white">
                        {uniqueItems}
                      </span>
                    )}
                  </button>
                )}
              </div>

              {/* Bottom row */}
              {/* Search */}
              <div className="w-full flex items-center">
                <div
                  onClick={() => {
                    const path = location.pathname;
                    const merchantMatch = path.match(/\/merchant-details\/(\w+)/);
                    if (merchantMatch) {
                      const merchantConfigId = merchantMatch[1];
                      navigate(`/${merchantConfigId}/search`);
                    } else {
                      navigate('/search');
                    }
                  }}
                  className='flex items-center input-shadowtwo h-10 px-3 gap-3 bg-white rounded w-full'
                  style={{ maxWidth: 'clamp(200px, 100%, 1000px)' }}
                >
                  <img src={SearchIcon} alt="SearchIcon" className='h-4 w-4' />
                  <input
                    type="search"
                    placeholder={t('searchitemsplachholder')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className='outline-none border-none w-full h-full flex-1 text-sm'
                  />
                </div>
              </div>
            </div>
          </>
        )}

        {isSearchPage && (
          <>
            <div className="lg:hidden w-full flex flex-row items-center gap-3 px-2 py-1">
              <button
                onClick={() => navigate('/home')}
                className="lg:hidden flex items-center justify-center min-w-[44px] min-h-[44px] mr-1"
                aria-label="Go back"
              >
                <FaChevronLeft className="text-lg opacity-70" />
              </button>

              {/* Search input */}
              <input
                type="search"
                placeholder={t('searchitemsplachholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className='outline-none border-none w-full h-12 flex-1 text-sm bg-transparent'
              />

              {/* Search icon */}
              <img
                src={SearchIcon}
                alt="SearchIcon"
                className='h-4 w-4 block'
              />

              {showFilterButton && (
                <button
                  onClick={handleFilterModal}
                  className="flex items-center justify-center min-w-[44px] min-h-[44px]"
                  aria-label={t('filter')}
                >
                  <img src={FilterIcon} alt="" className="w-5 h-5" />
                </button>
              )}
            </div>
          </>
        )}

        {isMerchantDetailsPage && (
          <div className="lg:hidden w-full flex items-center justify-between px-3 py-2">

            {/* Back button */}
            <button
              onClick={() => navigate('/home')}
              className="flex items-center justify-center min-w-[44px] min-h-[44px]"
              aria-label="Go back"
            >
              <FaArrowLeft />
            </button>

            {/* Store name */}
            <span className="text-sm font-semibold truncate max-w-[40%] sm:max-w-[55%] text-center">
              {storeName || ''}
            </span>

            {/* Search + Filter */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  if (merchantConfigId) {
                    navigate(`/${merchantConfigId}/search`);
                  }
                }}
                className="flex items-center justify-center min-w-[44px] min-h-[44px]"
                aria-label={t('search')}
              >
                <img src={SearchIcon} alt="" className="w-5 h-5" />
              </button>

              <button
                onClick={handleFilterModal}
                className="flex items-center justify-center min-w-[44px] min-h-[44px]"
                aria-label={t('filter')}
              >
                <img src={FilterIcon} alt="" className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {isCheckoutPage && (
          <div className="lg:hidden relative w-full py-3 px-3 flex items-center justify-center bg-white">
            {/* Back button */}
            <button
              onClick={() => navigate('/home')}
              className="absolute left-3 flex items-center justify-center min-w-[44px] min-h-[44px]"
              aria-label="Go back"
            >
              <FaArrowLeft />
            </button>

            {/* Title */}
            <h2 className='font-semibold text-xl text-[#000000DE] text-center'>{t('cart2')}</h2>
          </div>
        )}

        {isProfileOrdersPage && (
          <div className="lg:hidden relative w-full py-3 px-3 flex items-center justify-center bg-white">
            {/* Back button */}
            <button
              onClick={() => navigate('/profile')}
              className="absolute left-3 flex items-center justify-center min-w-[44px] min-h-[44px]"
              aria-label="Go back"
            >
              <FaArrowLeft />
            </button>

            {/* Title */}
            <h2 className='font-semibold text-xl text-[#000000DE] text-center'>{t('My Orders')}</h2>
          </div>
        )}

        {isProfileManageAddressPage && (
          <div className="lg:hidden relative w-full py-3 px-3 flex items-center justify-center bg-white">
            {/* Back button */}
            <button
              onClick={() => navigate('/profile')}
              className="absolute left-3 flex items-center justify-center min-w-[44px] min-h-[44px]"
              aria-label="Go back"
            >
              <FaArrowLeft />
            </button>

            {/* Title */}
            <h2 className='font-semibold text-xl text-[#000000DE] text-center'>{t('Manage addresses')}</h2>
          </div>
        )}

        {isProfileLanguagePage && (
          <div className="lg:hidden relative w-full py-3 px-3 flex items-center justify-center bg-white">
            {/* Back button */}
            <button
              onClick={() => navigate('/profile')}
              className="absolute left-3 flex items-center justify-center min-w-[44px] min-h-[44px]"
              aria-label="Go back"
            >
              <FaArrowLeft />
            </button>

            {/* Title */}
            <h2 className='font-semibold text-xl text-[#000000DE] text-center'>{t('Change language')}</h2>
          </div>
        )}

        {isMerchantCategoryPage && (
          <div className="lg:hidden relative w-full py-3 px-3 flex items-center justify-center bg-white">
            {/* Back button */}
            <button
              onClick={() => navigate('/home')}
              className="absolute left-3 flex items-center justify-center min-w-[44px] min-h-[44px]"
              aria-label="Go back"
            >
              <FaArrowLeft />
            </button>

            {/* Title */}
            <h2 className='font-semibold text-xl text-[#000000DE] text-center'>{categoryName}</h2>
          </div>
        )}

        {/* Fallback header mobile for uncovered pages */}
        {!isHomePage && !isSearchPage && !isMerchantDetailsPage && !isCheckoutPage && !isProfileOrdersPage && !isProfileManageAddressPage && !isProfileLanguagePage && !isMerchantCategoryPage && (
          <div className="lg:hidden relative w-full py-3 px-3 flex items-center justify-center bg-white">
            <button
              onClick={() => navigate(-1)}
              className="absolute left-3 flex items-center justify-center min-w-[44px] min-h-[44px]"
              aria-label="Go back"
            >
              <FaArrowLeft />
            </button>
            <h2 className='font-semibold text-lg text-[#000000DE] text-center'>Neobox</h2>
            {showFilterButton && (
              <button
                onClick={handleFilterModal}
                className="absolute right-3 flex items-center justify-center min-w-[44px] min-h-[44px]"
                aria-label={t('filter')}
              >
                <img src={FilterIcon} alt="" className="w-5 h-5" />
              </button>
            )}
          </div>
        )}

        {/* HEADER FOR DESKTOP */}
        {/* left — burger drawer removed; Header keeps location + brand only */}
        <div className='hidden lg:flex items-center gap-5'>
          <div
            className="flex items-center"
            style={{ gap: 'clamp(16px, 5vw, 80px)' }}
          >
            <Link to='/home'>
              <span className='text-lg relative left-2'>Neobox</span>
            </Link>

            {/* Select Location */}
            <div className="flex items-center w-full">
              <div
                className="flex items-center w-full max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg xl:max-w-xl 2xl:max-w-2xl cursor-pointer"
                onClick={handlelocationModal}
              >
                {/* Location Icon */}
                <img
                  src={LocationIcon}
                  alt="LocationIcon"
                  className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 mr-2"
                />

                {/* Location Info */}
                {user && user.locations && user.locations.length > 0 ? (
                  <div className="flex flex-col leading-tight min-w-0">
                    <p className="text-[8px] sm:text-[10px] md:text-[12px] font-semibold truncate">
                      {formatLineOne(user.locations[0])}
                    </p>
                    <p className="text-[8px] sm:text-[10px] md:text-[12px] text-black truncate">
                      {formatLineTwo(user.locations[0])}
                    </p>
                  </div>
                ) : (
                  <p className="text-[8px] sm:text-[10px] md:text-[12px] pt-1 pb-1 border-b border-dashed border-black break-words w-[80px] sm:w-[110px] md:w-[150px]">
                    {t('Select delivery location')}
                  </p>
                )}

                {/* Down Arrow */}
                <img
                  src={DownArrow}
                  alt="DownArrow"
                  className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 ml-2"
                />
              </div>
            </div>
          </div>
        </div>

        {/* middle */}
        <div className="hidden lg:flex flex-1 items-center justify-center">
          <div
            onClick={() => {
              const path = location.pathname;
              const merchantMatch = path.match(/\/merchant-details\/(\w+)/);
              if (merchantMatch) {
                const merchantConfigId = merchantMatch[1];
                navigate(`/${merchantConfigId}/search`);
              } else {
                navigate('/search');
              }
            }}
            className='flex items-center input-shadowtwo h-10 px-3 gap-3 bg-white rounded w-full'
            style={{ maxWidth: 'clamp(200px, 70%, 1000px)' }}
          >
            <img src={SearchIcon} alt="SearchIcon" className='h-4 w-4' />
            <input
              type="search"
              placeholder={t('searchitemsplachholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className='outline-none border-none w-full h-full flex-1 text-sm'
            />
          </div>
        </div>

        {/* right */}
        <div className='hidden lg:flex items-center'>
          {/* After Login  */}
          <div className='flex items-center lg:pl-2 lg:gap-3'>
            {/* Language switcher (desktop) — parity with mobile header */}
            <div className='relative' ref={desktopDropdownRef}>
              <button
                className='h-[42px] px-3 bg-black flex items-center gap-1 capitalize font-semibold rounded-lg text-sm text-white'
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                aria-label="Change language"
              >
                <img src={TranslatorIcon} alt="" className='h-4 w-4' />
                <span>{i18n.language === 'en' ? 'EN' : 'FR'}</span>
                <FaAngleDown className="text-xs" />
              </button>

              {isDropdownOpen && (
                <ul className='absolute right-0 py-2 bg-white top-12 min-w-[140px] rounded shadow-lg z-50'>
                  <li>
                    <button
                      className={`min-h-12 px-4 text-start font-normal mb-1 text-base transition-colors duration-300 w-full cursor-pointer ${i18n.language === 'en' ? 'bg-gray-200' : 'hover:bg-[#e0e0e0]'}`}
                      onClick={() => handleLanguageChange('en')}
                    >
                      English
                    </button>
                  </li>
                  <li>
                    <button
                      className={`min-h-12 px-4 text-start font-normal text-base transition-colors duration-300 w-full cursor-pointer ${i18n.language === 'fr' ? 'bg-gray-200' : 'hover:bg-[#e0e0e0]'}`}
                      onClick={() => handleLanguageChange('fr')}
                    >
                      Français
                    </button>
                  </li>
                </ul>
              )}
            </div>

            {/* Filter btn — parity with mobile header */}
            {showFilterButton && (
              <button
                onClick={handleFilterModal}
                className='flex items-center px-4 h-[42px] gap-3 font-semibold text-sm rounded-lg transition-colors duration-500 hover:bg-[#e5e7eb]'
                aria-label={t('filter')}
              >
                <img src={FilterIcon} alt="" />
                <span className='hidden md:block'>{t('filter')}</span>
              </button>
            )}

            {/* Profile btn  */}
            {((user && idToken) || adminImpersonate) && (
              <div>
                <button
                  onClick={() => navigate('/profile/orders')}
                  className='flex items-center px-3 h-[42px] gap-3 font-semibold text-sm rounded-lg transition-colors duration-500 hover:bg-[#e5e7eb]'
                >
                  <img src={ProfileIcon} alt="ProfileIcon" />
                  {t('profile')}
                </button>
              </div>
            )}

            {/* Cart btn (desktop) — modal rendered once at the header root so
                mobile cart button can also open it. */}
            {location.pathname !== '/checkout' && (
              <div>
                <button
                  onClick={handleCartModal}
                  className={`relative flex items-center px-3 h-[42px] gap-3 rounded-lg font-semibold text-sm transition-colors duration-500 hover:bg-[#e5e7eb] ${
                    uniqueItems > 0 ? 'bg-black text-white pl-3' : 'text-black'
                  }`}
                >
                  {uniqueItems > 0 ? (
                    <img src={CartIcon} alt="CartIcon" className="w-6 h-6" />
                  ) : (
                    <ShoppingCart className="w-6 h-6" />
                  )}
                  <span>{t('cart')}</span>
                  {uniqueItems > 0 && (
                    <span className='absolute -top-2 -right-2 bg-[#ff1e1e] w-[22px] h-[22px] flex items-center justify-center rounded-full text-xs border-2 border-white text-white'>
                      {uniqueItems}
                    </span>
                  )}
                </button>
              </div>
            )}
          </div>

          {/* Before login  */}
          {<div>
            {!(user && idToken) && !adminImpersonate  && (
              <button
                className='h-[42px] min-w-[80px] max-w-[100px] px-3 text-center capitalize text-sm hidden lg:block rounded-lg font-semibold transition-colors duration-500 hover:bg-[#e5e7eb]'
                onClick={handleToggleLoginModal}
              >
                {t('log in')}
              </button>
            )}

            <LoginModal
              isOpen={isLoginModalOpen}
              onClose={handleToggleLoginModal}
              onSubmit={(email) => {
                setUserEmail(email);
                handleToggleLoginModal();
                handleEmailSubmit(email);
              }}
            />

            <LoginModalHome
              isOpen={isLoginHomeModalOpen}
              onClose={handleToggleLoginModalHome}
              email={userEmail}
            />

            {isSignupModalOpen && (
              <SignupModal
                onClose={() => {
                  handleToggleSignupModal();
                  setPrefillData(null);
                }}
                email={userEmail}
                onAlreadyAccount={() => {
                  handleToggleSignupModal();
                  handleToggleLoginModal();
                }}
              />
            )}
          </div>}

        </div>

        {/* Mobile language switcher moved into mobile header top row */}
      </div>

      <SelectLocation
        onClose={handlelocationModal}
        isLocationModalOpen={isLocationModalOpen}
      />

      <FilterModal
        onClose={handleFilterModal}
        isFilterModalOpen={isFilterModalOpen}
        onApply={handleApplyFilters}
      />

      {location.pathname !== '/checkout' && (
        <CartModal
          onClose={handleCartModal}
          isCartModalOpen={isCartModalOpen}
        />
      )}

      <Toast message={toastMessage} type={toastType} />
    </header>
  );
};

export default Header;
